import { Badge, Button, Calendar, Tag } from 'antd';
import styled from 'styled-components';

import { Center } from '../../components/AuthLayout/styles';
import { media } from '../../styles/breakpoints';

type TagProps = {
  $color: string;
};

type CalendarProps = {
  $isLoading: boolean;
};

type FileBadgeProps = {
  $color: string;
};

type LegendButtonProps = {
  $isActive: boolean;
};

export const Container = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  position: relative;

  p {
    text-align: center;
    font-size: 16px;
    font-weight: bold;
    align-self: flex-start;
  }

  > .ant-spin.ant-spin-spinning {
    margin-top: 125px;
  }
`;

export const NoDataContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 32px 20px;
  background: #fff;
  border-radius: 16px;
  border: 1px solid #f0f0f0;

  p {
    font-size: 15px;
    color: rgba(0, 0, 0, 0.55);
    text-align: center;
    margin-bottom: 20px;
    font-weight: 400 !important;
  }

  img {
    opacity: 0.8;
  }
`;

export const TimeLine = styled.ul`
  width: 100%;
  border-left: 4px solid var(--gray-200);
  border-top-left-radius: 4px;
  border-bottom-left-radius: 4px;
  padding-left: 0;
  margin: 0;
`;

export const TimeLineItem = styled.li`
  margin: 12px 0;
  padding: 14px 16px;
  background-color: #fff;
  border-top-right-radius: 12px;
  border-bottom-right-radius: 12px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  cursor: pointer;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.06);
  transition: all 0.2s;
  list-style: none;

  &:active {
    background-color: #fafafa;
    transform: translateX(2px);
  }

  > span:first-child {
    font-weight: 600;
    font-size: 14px;
    color: rgba(0, 0, 0, 0.7);
    min-width: 55px;
  }

  > span:nth-child(2) {
    flex: 1;
    font-size: 14px;
    color: rgba(0, 0, 0, 0.85);
    padding: 0 12px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
`;

export const TagStyled = styled.div<TagProps>`
  min-width: 85px;
  padding: 4px 8px;
  background-color: ${({ $color }) => $color};
  text-align: center;
  border-radius: 6px;
  font-size: 10px;
  font-weight: 600;
  color: var(--white);
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 4px;
  flex-shrink: 0;

  svg {
    width: 11px;
    height: 11px;
  }
`;

export const CalendarStyled = styled(Calendar)<CalendarProps>`
  .ant-radio-group.ant-radio-group-outline.ant-picker-calendar-mode-switch {
    display: none;
  }
  border: 2px solid var(--gray-50);
  padding: 0 10px;
  border-radius: 4px;
  filter: ${({ $isLoading }) => ($isLoading ? 'blur(3px)' : 'none')};

  tr td:not(.ant-picker-cell-in-view) .ant-picker-calendar-date-value,
  tr td:not(.ant-picker-cell-in-view) .ant-picker-calendar-date-content {
    opacity: 0;
  }
`;

export const CalendarCellStyled = styled.div`
  position: absolute;
  top: 34px;
  bottom: 0;
  left: 0;
  right: 0;
  flex-direction: column;
  display: flex;
  overflow-y: auto;
  gap: 2px;
  padding: 5px;
  text-align: center;
  align-items: stretch;

  > span:hover {
    box-shadow: 0px 0px 7px rgb(0 0 0 / 25%);
  }
`;

export const Legend = styled.div`
  position: absolute;
  left: 20px;
  top: 20px;
  display: flex;
  gap: 30px;
  z-index: 10;

  .ant-badge-status-dot {
    border-radius: 0;
    height: 12px;
    width: 12px;
  }

  .ant-badge-status-text {
    font-size: 13px;
  }
`;

export const LegendIconItem = styled.div`
  display: flex;
  align-items: center;
  font-size: 13px;
  color: rgba(0, 0, 0, 0.85);

  svg {
    margin-right: 8px;
  }
`;

export const CellTagStyled = styled(Tag)`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 5px;
  font-weight: 600;
`;

export const LoadingContainer = styled(Center)`
  height: calc(100vh - 100px);
  position: absolute;
  z-index: 1;
`;

export const FileBadge = styled(Badge).attrs({
  size: 'small',
  style: {
    backgroundColor: 'white',
    fontWeight: 600,
  },
})<FileBadgeProps>`
  .ant-badge-count {
    color: ${({ $color }) => {
      return $color;
    }} !important;
  }
`;

export const FilterButtonsContainer = styled.div`
  display: flex;

  > button {
    display: flex;
    align-items: center;
    gap: 4px;
  }
`;

export const DownloadedButton = styled(Button)<LegendButtonProps>`
  border-top-right-radius: 0px;
  border-bottom-right-radius: 0px;
  border-color: ${({ $isActive }) =>
    $isActive ? 'var(--success);' : '#d9d9d9'};
  border-right-color: ${({ $isActive }) =>
    $isActive ? 'var(--success)' : '#fff'};
  color: ${({ $isActive }) =>
    $isActive ? 'var(--success)' : 'rgba(0, 0, 0, 0.85)'};

  &:hover,
  &:focus {
    color: ${({ $isActive }) =>
      $isActive ? 'var(--success)' : 'rgba(0, 0, 0, 0.85)'};
    border-color: ${({ $isActive }) =>
      $isActive ? 'var(--success);' : '#d9d9d9'};
  }
`;
export const NonDownloadedButton = styled(Button)<LegendButtonProps>`
  border-radius: 0px;
  border-color: ${({ $isActive }) => ($isActive ? 'var(--info);' : '#d9d9d9')};
  color: ${({ $isActive }) =>
    $isActive ? 'var(--info)' : 'rgba(0, 0, 0, 0.85)'};

  &:hover,
  &:focus {
    border-color: ${({ $isActive }) =>
      $isActive ? 'var(--info);' : '#d9d9d9'};
    color: ${({ $isActive }) =>
      $isActive ? 'var(--info)' : 'rgba(0, 0, 0, 0.85)'};
  }
`;
export const ExpiredButton = styled(Button)<LegendButtonProps>`
  border-top-left-radius: 0px;
  border-bottom-left-radius: 0px;
  border-color: ${({ $isActive }) => ($isActive ? 'var(--danger)' : '#d9d9d9')};
  border-left-color: ${({ $isActive }) =>
    $isActive ? 'var(--danger)' : '#fff'};
  color: ${({ $isActive }) =>
    $isActive ? 'var(--danger)' : 'rgba(0, 0, 0, 0.85)'};

  &:hover,
  &:focus {
    border-color: ${({ $isActive }) =>
      $isActive ? 'var(--danger);' : '#d9d9d9'};
    color: ${({ $isActive }) =>
      $isActive ? 'var(--danger)' : 'rgba(0, 0, 0, 0.85)'};
  }
`;

export const WelcomeSection = styled.div`
  width: 100%;
  margin-bottom: 24px;

  ${media.md} {
    margin-bottom: 28px;
  }
`;

export const WelcomeTitle = styled.h1`
  font-size: 24px;
  font-weight: 600;
  color: rgba(0, 0, 0, 0.85);
  margin: 0 0 4px 0;

  ${media.md} {
    font-size: 26px;
    font-weight: 700;
    margin-bottom: 6px;
  }
`;

export const WelcomeSubtitle = styled.p`
  font-size: 14px;
  color: rgba(0, 0, 0, 0.45);
  margin: 0 0 24px 0;
  text-align: left;
  font-weight: normal;

  ${media.md} {
    font-size: 15px;
    margin: 0;
    color: rgba(0, 0, 0, 0.55);
  }
`;

export const QuickAccessTitle = styled.h2`
  font-size: 20px;
  font-weight: 600;
  color: rgba(0, 0, 0, 0.85);
  margin: 0 0 12px 0;
`;

export const QuickAccessCards = styled.div`
  display: flex;
  gap: 16px;
  flex-wrap: wrap;

  ${media.md} {
    flex-direction: column;
    gap: 12px;
  }
`;

export const QuickAccessCard = styled.div`
  display: flex;
  align-items: center;
  padding: 24px 20px;
  background: #fff;
  border: 1px solid #f0f0f0;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s ease-in-out;
  width: calc(33.333% - 11px);
  min-width: 280px;
  min-height: 100px;

  ${media.lg} {
    width: calc(50% - 8px);
  }

  ${media.md} {
    width: 100%;
    min-width: unset;
    padding: 16px;
    min-height: 80px;
  }

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
    border-color: #e8e8e8;
  }

  &:active {
    transform: translateY(0);
  }

  ${media.md} {
    &:hover {
      transform: none;
    }

    &:active {
      background: #fafafa;
    }
  }
`;

export const QuickAccessIcon = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 8px;
  background: #f9f0ff;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 12px;

  svg {
    width: 20px;
    height: 20px;
    color: #722ed1;
  }
`;

export const QuickAccessContent = styled.div`
  flex: 1;
`;

export const QuickAccessLabel = styled.div`
  font-size: 14px;
  font-weight: 500;
  color: rgba(0, 0, 0, 0.85);
`;

export const QuickAccessDescription = styled.div`
  font-size: 12px;
  color: rgba(0, 0, 0, 0.45);
`;

export const QuickAccessBadge = styled.div`
  margin-left: auto;
  margin-right: 8px;
`;

export const QuickAccessArrow = styled.div`
  color: rgba(0, 0, 0, 0.25);

  svg {
    width: 16px;
    height: 16px;
  }
`;

export const CalendarSectionHeader = styled.div`
  width: 100%;
  margin-bottom: 16px;
`;

export const CalendarSectionTitle = styled.h2`
  font-size: 20px;
  font-weight: 600;
  color: rgba(0, 0, 0, 0.85);
  margin: 0 0 4px 0;
`;

export const CalendarSectionSubtitle = styled.p`
  font-size: 14px;
  color: rgba(0, 0, 0, 0.45);
  margin: 0;
  text-align: left;
  font-weight: normal;
`;

// Mobile month navigation
export const MobileMonthNav = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: #fff;
  border-radius: 16px;
  padding: 14px 12px;
  margin-bottom: 20px;
  border: 1px solid #f0f0f0;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
`;

export const MobileMonthButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 48px;
  height: 48px;
  border: none;
  background: #f5f5f5;
  border-radius: 12px;
  cursor: pointer;
  color: rgba(0, 0, 0, 0.7);
  transition: all 0.2s;

  &:active {
    background: #e8e8e8;
    transform: scale(0.95);
  }

  svg {
    width: 22px;
    height: 22px;
  }
`;

export const MobileMonthLabel = styled.div`
  font-size: 18px;
  font-weight: 700;
  color: rgba(0, 0, 0, 0.88);
  text-transform: capitalize;
  letter-spacing: -0.3px;
`;

// Mobile quick actions
export const MobileQuickActions = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin-bottom: 36px;
`;

export const MobileQuickAction = styled.div`
  display: flex;
  align-items: center;
  width: 100%;
  padding: 18px 16px;
  background: #fff;
  border: 1px solid #f0f0f0;
  border-radius: 14px;
  cursor: pointer;
  transition: all 0.2s;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);

  &:active {
    background: #fafafa;
    transform: scale(0.98);
  }
`;

export const MobileQuickActionIcon = styled.div`
  width: 52px;
  height: 52px;
  border-radius: 14px;
  background: linear-gradient(135deg, #f9f0ff 0%, #efdbff 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 16px;
  flex-shrink: 0;

  svg {
    width: 26px;
    height: 26px;
    color: #722ed1;
  }
`;

export const MobileQuickActionContent = styled.div`
  flex: 1;
  min-width: 0;
`;

export const MobileQuickActionLabel = styled.div`
  font-size: 16px;
  font-weight: 600;
  color: rgba(0, 0, 0, 0.88);
  letter-spacing: -0.2px;
`;

export const MobileQuickActionDescription = styled.div`
  font-size: 13px;
  color: rgba(0, 0, 0, 0.5);
  margin-top: 4px;
`;

export const MobileQuickActionBadge = styled.div`
  margin-left: 12px;
  flex-shrink: 0;

  .ant-badge-count {
    font-size: 13px;
    font-weight: 600;
    min-width: 24px;
    height: 24px;
    line-height: 24px;
    border-radius: 12px;
    box-shadow: 0 2px 4px rgba(245, 34, 45, 0.3);
  }
`;

export const MobileSectionTitle = styled.h2`
  font-size: 20px;
  font-weight: 700;
  color: rgba(0, 0, 0, 0.88);
  margin: 0 0 16px 0;
  letter-spacing: -0.3px;
`;

export const MobileQuickAccessLabel = styled.h3`
  font-size: 13px;
  font-weight: 600;
  color: rgba(0, 0, 0, 0.45);
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin: 0 0 12px 0;
`;

// Skeleton loading styles
const shimmer = `
  @keyframes shimmer {
    0% {
      background-position: -200px 0;
    }
    100% {
      background-position: calc(200px + 100%) 0;
    }
  }
`;

export const SkeletonBase = styled.div`
  ${shimmer}
  background: linear-gradient(90deg, #f0f0f0 0px, #e8e8e8 40px, #f0f0f0 80px);
  background-size: 200px 100%;
  animation: shimmer 1.5s ease-in-out infinite;
  border-radius: 6px;
`;

export const SkeletonCard = styled(SkeletonBase)`
  height: 88px;
  border-radius: 14px;
  width: 100%;
`;

export const SkeletonMonthNav = styled(SkeletonBase)`
  height: 76px;
  border-radius: 16px;
  width: 100%;
  margin-bottom: 20px;
`;

export const SkeletonTimelineItem = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px 16px;
  background: #fff;
  border-radius: 12px;
  margin: 12px 0;
  margin-left: 4px;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.06);
`;

export const SkeletonText = styled(SkeletonBase)<{ $width?: string; $height?: string }>`
  width: ${({ $width }) => $width || '100%'};
  height: ${({ $height }) => $height || '14px'};
`;

export const SkeletonCircle = styled(SkeletonBase)`
  width: 52px;
  height: 52px;
  border-radius: 14px;
  flex-shrink: 0;
`;

export const SkeletonContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

export const SkeletonTimelineContainer = styled.div`
  border-left: 4px solid var(--gray-200);
  border-top-left-radius: 4px;
  border-bottom-left-radius: 4px;
`;
